import type { Middleware } from 'openapi-fetch';
import type { TokenProvider } from './auth';

/**
 * Sessions that survive a failed request (SDD-019 S-6) — the legacy SDK's interceptor
 * (`sdk-generator` `baseApiFront.mustache`, `attachInterceptor`), with its bugs fixed.
 *
 * Refresh-AHEAD-of-expiry already happens in `tokenProvider`. Some requests fail anyway: the
 * token was revoked, the clocks disagree, it expired in flight, or a role was granted after
 * sign-in (grants live in the token, T-14). This middleware is the second line:
 *
 * | The response | What happens |
 * |---|---|
 * | 401 or 403 | ONE refresh, shared by every concurrent caller; the request is replayed ONCE with the new `Authorization`. A second 401/403 is returned as is. |
 * | refresh fails | the original response is returned and `onSessionLost` runs (once per failed refresh). |
 * | network error (no response) | retried WITHOUT a refresh, at most `maxNetworkRetries` (4) times per request, after 250 ms, 500 ms, 1 s, 2 s; `tokenProvider` is asked again before each try. Only for requests that are safe to send twice: GET/HEAD/OPTIONS, or one carrying `If-Match` (T-12) or `Idempotency-Key`. |
 * | a session endpoint | never touched — no refresh, no retry (the legacy used a separate axios instance for this). |
 *
 * The legacy, for comparison: it refreshed on every network error, retried instantly, counted
 * retries in a module-global that was never reset, and replayed any method.
 *
 * The body is replayed from a `Request.clone()` taken in `onRequest`, so a POST goes out
 * byte-identical. The middleware is installed LAST by `createEisClient` (OQ-40): its `onRequest`
 * then clones the fully decorated request (bearer, traceparent, If-Match), and its `onResponse`
 * runs FIRST, so the ETag middleware sees the replayed response, not the 401 it replaced.
 */
export interface SessionRefreshOptions {
  /**
   * Refreshes the session; resolves the new access token, or null when there is no session any
   * more. Supplied by the active auth adapter — the SDK does not know whether it calls authservice
   * (`session:refresh`) or the legacy `/authservice/v1/refresh-token` (S-11).
   */
  readonly refresh: () => Promise<string | null>;
  /** True for a URL this middleware must never act on: the session endpoints themselves. */
  readonly isSessionEndpoint?: (url: string) => boolean;
  /** Runs once per failed refresh: the adapter's "this window has no session any more". */
  readonly onSessionLost?: () => void;
  /** Network-error retries per request. Default 4. */
  readonly maxNetworkRetries?: number;
  /** Asked again before each network retry, so a token due for refresh-ahead is refreshed then. */
  readonly tokenProvider?: TokenProvider;
  /** The waits before each network retry. Default 250, 500, 1000, 2000 ms. */
  readonly backoffMs?: readonly number[];
}

export const DEFAULT_NETWORK_BACKOFF_MS: readonly number[] = [250, 500, 1000, 2000];
export const DEFAULT_MAX_NETWORK_RETRIES = 4;

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * A request is safe to send again after a network error only if running it twice cannot do
 * anything running it once did not: a read, or a write the server can recognise as a repeat —
 * `If-Match` (a second apply fails the precondition instead of applying twice) or
 * `Idempotency-Key`. Anything else may already have run on the server.
 */
export function isSafeToReplay(request: Request): boolean {
  return (
    SAFE_METHODS.has(request.method.toUpperCase()) ||
    request.headers.has('If-Match') ||
    request.headers.has('Idempotency-Key')
  );
}

interface InFlight {
  /** The request as it left the middleware chain, unread — every replay clones THIS again. */
  readonly original: Request;
  refreshed: boolean;
  networkRetries: number;
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export function createSessionRefreshMiddleware(options: SessionRefreshOptions): Middleware {
  const maxNetworkRetries = options.maxNetworkRetries ?? DEFAULT_MAX_NETWORK_RETRIES;
  const backoff = options.backoffMs ?? DEFAULT_NETWORK_BACKOFF_MS;
  const isSessionEndpoint = options.isSessionEndpoint ?? (() => false);
  /** Per request, by openapi-fetch's request id: the counter is the REQUEST's, never global. */
  const inFlight = new Map<string, InFlight>();
  /** One refresh at a time for every request of this client (single flight). */
  let refreshing: Promise<string | null> | null = null;

  const refreshOnce = (): Promise<string | null> => {
    refreshing ??= (async () => {
      let token: string | null = null;
      try {
        token = await options.refresh();
      } catch {
        token = null;
      }
      if (token === null || token === '') {
        options.onSessionLost?.();
        return null;
      }
      return token;
    })().finally(() => {
      refreshing = null;
    });
    return refreshing;
  };

  const replay = async (
    state: InFlight,
    send: (request: Request) => Promise<Response>,
    token: string | null | undefined,
  ): Promise<Response> => {
    const again = state.original.clone();
    if (token !== null && token !== undefined && token !== '') {
      again.headers.set('Authorization', `Bearer ${token}`);
    }
    return send(again);
  };

  return {
    onRequest({ request, id }) {
      if (isSessionEndpoint(request.url)) return undefined;
      inFlight.set(id, { original: request.clone(), refreshed: false, networkRetries: 0 });
      return undefined;
    },

    async onResponse({ response, id, options: client }) {
      const state = inFlight.get(id);
      if (state === undefined) return undefined;
      try {
        if ((response.status !== 401 && response.status !== 403) || state.refreshed) {
          return undefined;
        }
        state.refreshed = true;
        const token = await refreshOnce();
        // No session any more: the caller gets the response it would have got anyway.
        if (token === null) return undefined;
        return await replay(state, client.fetch, token);
      } finally {
        inFlight.delete(id);
      }
    },

    async onError({ request, error, id, options: client }) {
      const state = inFlight.get(id);
      if (state === undefined) return undefined;
      if (isAbort(error) || !isSafeToReplay(request)) {
        inFlight.delete(id);
        return undefined;
      }
      while (state.networkRetries < maxNetworkRetries) {
        await sleep(backoff[state.networkRetries] ?? backoff.at(-1) ?? 0);
        state.networkRetries += 1;
        try {
          // No forced refresh: a network error is not an auth failure. The provider refreshes
          // ahead of expiry on its own if the token is due.
          const token =
            options.tokenProvider === undefined ? undefined : await options.tokenProvider();
          // The response goes on through onResponse (this one included: a 401 there is refreshed).
          return await replay(state, client.fetch, token);
        } catch (retryError) {
          if (isAbort(retryError)) break;
        }
      }
      inFlight.delete(id);
      // The ORIGINAL error reaches the caller.
      return undefined;
    },
  };
}
